import React from 'react';
import { Row, Col, Container } from 'reactstrap';
import _ from 'lodash';
import PropTypes from 'prop-types';
import qs from 'qs';
import Router from 'next/router';
import HeadElement from 'components/HeadElement';
//TODO remove html-to-text from dependenceies

import BannerTemplate from './template-components/BannerComponent';
import SwiperBanner from '../SwiperBannerComponent';
import ImageBoxLinkTemplate from './template-components/ImageTileLinks';
import ArticleCarousel, {
  loadCarouselArticles,
} from '../article/ArticleCarousel';
import FilterPanel from '../filters/FilterPanel';
import FilterPanelModal from '../filters/FilterPanelModal';
import Brands from 'components/Brands';
import Suppliers from 'components/Suppliers';
import loginAware from 'components/LoginAwareComponent';
import ArticleListComponent from 'components/article/ArticleListComponent';
import BlockGroups from './template-components/ImageGroupComponent';
import PartnersDisplay from './template-components/PartnersDisplayComponent';
import BlockCarousel from './template-components/ImageCarouselComponent';
import BlockTextAndImage from './template-components/TextAndImageComponent';
import ArticleCarouselWithImage from './template-components/CarouselAndImageComponent';

const componentMap = {
  HTML: col => (
    <div
      key={col.id}
      // eslint-disable-next-line
      dangerouslySetInnerHTML={{ __html: col.html }}
    />
  ),
  PartnersDisplay: col => (
    <PartnersDisplay key={col.id} partnersDisplay={col.partnersDisplay} />
  ),
  BlockCarousel: (col, carouselConfigs, shopConfig, isFluid) => (
    <BlockCarousel
      key={col.id}
      blockCarousel={col.blockCarousel}
      isFluid={isFluid}
    />
  ),
  BlockGroup: col => <BlockGroups key={col.id} blockGroup={col.blockGroup} />,
  ArticleCarouselWithImage: col => (
    <ArticleCarouselWithImage
      key={col.id}
      articleCarouselWithImage={col.articleCarouselWithImage}
    />
  ),
  BlockTextAndImage: col => (
    <BlockTextAndImage key={col.id} blockTextAndImage={col.blockTextAndImage} />
  ),
  Image: col => <img key={col.id} src={col.image} alt=".." className="w-100" />,
  Banner: col => <BannerTemplate key={col.id} banner={col.banner} />,
  SwiperBanner: col => <SwiperBanner key={col.id} value={col.swiperBanner} />,
  ShopTiles: col => (
    <div key={col.id}>
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: col.tilesHeader }}
        className="text-center tiles-header-custom"
      />
      <div
        className="text-center mx-auto mb-4"
        style={{ borderTop: '1px solid black', width: '150px' }}
      />
      <ImageBoxLinkTemplate layout={col} />
    </div>
  ),
  Carousel: col => (
    <>
      {col.carousels.map(c => (
        <ArticleCarousel key={c.id} carousel={c} />
      ))}
    </>
  ),
  FilterPanel: (col, carouselConfigs, shopConfig) => {
    return (
      <div key={col.id}>
        <div className="d-inline d-lg-none mt-1 mb-3 text-center">
          <FilterPanelModal
            shopConfig={shopConfig}
            onChange={querySpecification => {
              Router.push({
                pathname: '/articles',
                query: {
                  filter: qs.stringify(querySpecification),
                },
              });
            }}
          />
        </div>
        <div className="d-none d-lg-block p-3">
          <FilterPanel
            shopConfig={shopConfig}
            onChange={querySpecification => {
              Router.push({
                pathname: '/articles',
                query: {
                  filter: qs.stringify(querySpecification),
                },
              });
            }}
          />
        </div>
      </div>
    );
  },
  BrandsList: col => <Brands key={col.id} initialBrands={col.initialBrands} />,
  SuppliersList: col => (
    <Suppliers key={col.id} initialSuppliers={col.initialSuppliers} />
  ),
  ArticlesList: col => {
    return (
      <ArticleListComponent
        key={col.id}
        articleListControllerConfig={col.articleList}
      />
    );
  },
};

// TODO do we need carouselConfigs here?
function ColComponent({ col, shopConfig, carouselConfigs, isFluid }) {
  return (
    <Col className={col.className} key={col.id}>
      {col.component &&
        componentMap[col.component](col, carouselConfigs, shopConfig, isFluid)}
    </Col>
  );
}

function RowColTemplate({ pageConfig, shopConfig, asPath }) {
  return (
    <div id="content">
      <HeadElement
        shopConfig={shopConfig}
        asPath={asPath}
        pageConfig={pageConfig}
      />
      {pageConfig.layout.rows.map(row => (
        <Container
          fluid={row.fluid}
          key={row.id}
          className={row.fluid ? 'px-0' : ''}
        >
          <Row className={`${row.className || ''} ${row.fluid ? 'g-0' : ''}`}>
            {row.cols.map(col => (
              <ColComponent
                col={col}
                key={col.id}
                shopConfig={shopConfig}
                isFluid={row.fluid}
              />
            ))}
          </Row>
        </Container>
      ))}
    </div>
  );
}

const loadCarouselData = async (carouselConfig, req) => {
  carouselConfig.config = { ...carouselConfig }; // right now this is for backward compatibility
  carouselConfig.articles = await loadCarouselArticles(carouselConfig, req);
  return carouselConfig;
};

RowColTemplate.getInitialProps = async ({
  req,
  pageConfig,
  shopConfig,
  asPath,
}) => {
  let carousels = [];
  let carouselCols = [];

  for (const row of pageConfig.layout.rows) {
    for (const col of row.cols) {
      if (col.carousels) {
        carouselCols.push(col); // these are cols that can have multiple carousels
      }
      if (col.articleCarouselWithImage) {
        col.articleCarouselWithImage.articles = await loadCarouselArticles(
          { query: col.articleCarouselWithImage.query, pageSize: 6 },
          req,
        );
      }
      if (col.component === 'BrandsList') {
        const props = await Brands.getInitialProps({
          req,
          querySpecification: {},
          pageConfig,
        });
        col.initialBrands = props.initialBrands;
      }
      if (col.component === 'SuppliersList') {
        const props = await Suppliers.getInitialProps({
          req,
          querySpecification: {},
          pageConfig,
        });
        col.initialSuppliers = props.initialSuppliers;
      }
    }
  }

  const carouselConfigs = _.flatten(carouselCols.map(x => x.carousels));
  if (carouselConfigs) {
    carousels = await Promise.all(
      carouselConfigs.map(config => loadCarouselData(config, req)),
    );
  }

  return {
    shopConfig,
    pageConfig,
    asPath,
    ...pageConfig,
  };
};

RowColTemplate.propTypes = {
  carousels: PropTypes.array,
};
RowColTemplate.defaultProps = {
  carousels: [],
};

export default loginAware(RowColTemplate);
